/* ---------------------------------------------------------------------------
   The weather, drawn: a sky for a day's return, and the glyph that names it.

   ONE DRAWING FOR EVERY PLACE A DAY'S WEATHER IS SHOWN — the return's picker,
   the calendars, the returns list, the build playback — so a heavy-rain day
   looks the same wherever somebody meets it. The sky is a gradient with its
   weather in it (sun, cloud, rain, lightning, heat haze), coloured from the
   `--wx-*` tokens in src/styles/tokens.css and animated in app.css.

   THE SKY IS DECORATION; THE WORDS ARE THE RECORD. Every use prints or titles
   `weatherLabel` beside it, so nothing depends on reading a colour.

   STILL UNLESS ASKED. A month of animated rain is thirty repainting cells and
   a distraction from the figures, so a sky moves only where it is `live` — the
   picked tile, a playback card — or under the pointer.
   --------------------------------------------------------------------------- */

import { h, icon, type IconPath } from '../core/dom.ts';
import { WEATHER, isWeather, weatherLabel, weatherObstructs } from '../data/siteReturn.ts';

/** The sun's disc and rays, centred on (12, 12). */
const SUN = [
  'M16 12a4 4 0 1 1-8 0a4 4 0 1 1 8 0Z',
  'M12 3v2',
  'M12 19v2',
  'M3 12h2',
  'M19 12h2',
  'M5.6 5.6l1.4 1.4',
  'M17 17l1.4 1.4',
  'M5.6 18.4L7 17',
  'M17 7l1.4-1.4',
];

/** A cloud raised to leave room for what falls from it. */
const HIGH_CLOUD = 'M7 15h10.5a4 4 0 0 0 .5-7.97A6 6 0 0 0 6.2 8.1 3.5 3.5 0 0 0 7 15Z';

const GLYPHS: Readonly<Record<string, IconPath>> = {
  'not-recorded': [
    'M21 12a9 9 0 1 1-18 0a9 9 0 1 1 18 0Z',
    'M9.5 9.5a2.5 2.5 0 1 1 3.4 2.33c-.55.22-.9.75-.9 1.34V14',
    'M12 17.5h.01',
  ],
  clear: SUN,
  overcast: ['M7 19h10.5a4.5 4.5 0 0 0 .6-8.96A6.5 6.5 0 0 0 5.7 11.2 4 4 0 0 0 7 19Z'],
  rain: [HIGH_CLOUD, 'M9 18l-1 2.5', 'M13 18l-1 2.5', 'M17 18l-1 2.5'],
  'heavy-rain': [HIGH_CLOUD, 'M8.5 17.5l-1.5 4', 'M12 17.5l-1.5 4', 'M15.5 17.5l-1.5 4', 'M19 17.5l-1 2.5'],
  storm: [HIGH_CLOUD, 'M13 15l-2.5 4h3l-2 4', 'M8 17l-1 2.5', 'M17.5 17l-1 2.5'],
  'extreme-heat': [
    'M15.5 9a3.5 3.5 0 1 1-7 0a3.5 3.5 0 1 1 7 0Z',
    'M12 2.5v1.5',
    'M5.5 9H4',
    'M20 9h-1.5',
    'M7.4 4.4l1 1',
    'M16.6 4.4l-1 1',
    'M4 16c1.3-1 2.7-1 4 0s2.7 1 4 0 2.7-1 4 0 2.7 1 4 0',
    'M4 20c1.3-1 2.7-1 4 0s2.7 1 4 0 2.7-1 4 0 2.7 1 4 0',
  ],
};

/** The id a drawing is keyed by — an id this register does not know is drawn as not recorded. */
export function weatherKey(id: string): string {
  return isWeather(id) ? id : 'not-recorded';
}

/** The weather's glyph, in the current text colour. */
export function weatherGlyph(id: string, size = 18): SVGSVGElement {
  return icon(GLYPHS[weatherKey(id)]!, { size, width: 1.6, class: 'wx-glyph' });
}

/**
 * The sky itself: a decorative layer that fills whatever holds it.
 *
 * The holder carries `data-wx` (see `wxAttrs`), which sets the colours for
 * both the sky and anything drawn over it, and positions it.
 */
export function weatherSky(live = false): HTMLElement {
  return h(
    'span',
    { class: ['wx', live && 'wx--live'], 'aria-hidden': 'true' },
    h('span', { class: 'wx__fx wx__fx--a' }),
    h('span', { class: 'wx__fx wx__fx--b' }),
  );
}

/** The attribute that scopes a holder to one weather's colours. */
export function wxAttrs(id: string): { 'data-wx': string } {
  return { 'data-wx': weatherKey(id) };
}

/**
 * A day's weather as a small pill: its sky, its glyph, its name — and "lost"
 * where it is weather that ordinarily stops work, since that is what a reader
 * scans for.
 */
export function weatherChip(
  id: string,
  opts: { live?: boolean; detail?: string | null; lost?: boolean } = {},
): HTMLElement {
  return h(
    'span',
    { class: 'wxchip', ...wxAttrs(id), title: weatherLabel(id) },
    weatherSky(opts.live),
    weatherGlyph(id, 15),
    h('span', { class: 'wxchip__label', text: weatherLabel(id) }),
    opts.detail ? h('span', { class: 'wxchip__detail', text: opts.detail }) : null,
    opts.lost !== false && weatherObstructs(id) ? h('span', { class: 'wxchip__lost', text: 'lost' }) : null,
  );
}

/**
 * The daily return's weather: a tile per weather, one to be picked.
 *
 * `not-recorded` IS NOT OFFERED. Whoever writes the day's return is on site
 * and says what the sky did; see `saveSiteReturn` in src/core/store.ts. A
 * return already on record without it arrives with no tile picked.
 *
 * Real radio buttons under the tiles, so the arrow keys move through them.
 */
export function weatherPicker(value: string, onPick: (id: string) => void, name = 'ret-weather'): HTMLElement {
  return h(
    'div',
    { class: 'wxpick', role: 'radiogroup', 'aria-label': 'Weather', 'aria-required': 'true' },
    ...WEATHER.filter((w) => w.id !== 'not-recorded').map((w) =>
      h(
        'label',
        { class: ['wxpick__opt', w.id === value && 'wxpick__opt--on'], ...wxAttrs(w.id), title: w.note ?? w.label },
        h('input', {
          type: 'radio',
          class: 'wxpick__radio',
          id: `${name}-${w.id}`,
          name,
          value: w.id,
          checked: w.id === value,
          onchange: () => onPick(w.id),
        }),
        weatherSky(w.id === value),
        weatherGlyph(w.id, 26),
        h('span', { class: 'wxpick__label', text: w.label }),
        w.obstructive ? h('span', { class: 'wxpick__note', text: 'Stops work' }) : null,
      ),
    ),
  );
}
